import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { FaChartBar, FaUser, FaEnvelope, FaLock } from 'react-icons/fa';
import { FaCow } from "react-icons/fa6";
import { MdMobileFriendly } from "react-icons/md";
import './SignUp.css';
import { Signup } from '../../api/signupApi';
import SocialLogin from '../../SocialLogin/SocialLogin';
import { AuthContext } from '../../Contexts/AuthContext';

export default function SignUp() {
    const {user, login} = useContext(AuthContext);
    const [checking, setChecking] = useState(true);
    const [formData, setFormData] = useState({ 
        nom: '', 
        email: '', 
        password: '',
        'h-captcha-response': ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const captchaRef = useRef(null);

    useEffect(() => {
            const token = localStorage.getItem("token");
            if (token && user) {
                navigate("/" + user.role);
            } else {
                setChecking(false);
            }
        }, [user, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCaptchaVerify = (token) => {
        setFormData({ ...formData, 'h-captcha-response': token });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData['h-captcha-response']) {
            setError('Veuillez compléter la vérification de sécurité');
            return;
        }

        setLoading(true);

        try {
            const response = await Signup(formData);
            console.log('Successfully registered:', response);
            
            setSuccess('Inscription réussie! Veuillez vérifier votre email pour activer votre compte.');
            
            if (captchaRef.current) {
                captchaRef.current.resetCaptcha();
            }

            setTimeout(() => {
                navigate('/verification', { 
                    state: { email: formData.email } 
                });
            }, 3000);

        } catch (error) {
            console.error('Error:', error);
            
            if (captchaRef.current) {
                captchaRef.current.resetCaptcha();
            }
            setFormData({ ...formData, 'h-captcha-response': '' });
            
            if (error.response?.data?.errors) {
                const errors = Object.values(error.response.data.errors).flat();
                setError(errors.join(', '));
            } else if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('Un problème est survenu, merci de réessayer');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="signup-page">
            <div className="signup-left">
                <div className="signup-content">
                    <div className="logo-section">
                        <div className="logo-icon"><FaCow /></div>
                        <h1 className="logo-text">Gestion des Animaux</h1>
                    </div>

                    <div className="welcome-text">
                        <h2>Créer un compte</h2>
                        <p>Rejoignez-nous pour gérer votre élevage</p>
                    </div>

                    {error && (
                        <div className="error-message">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="success-message">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="signup-form">
                        <div className="input-group">
                            <label htmlFor="nom">Nom</label>
                            <div className="input-wrapper">
                                <FaUser className="input-icon" />
                                <input
                                    id="nom"
                                    className="input-field"
                                    type="text"
                                    name="nom"
                                    placeholder="Votre nom complet..."
                                    value={formData.nom}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="email">Email</label>
                            <div className="input-wrapper">
                                <FaEnvelope className="input-icon" />
                                <input
                                    id="email"
                                    className="input-field"
                                    type="email"
                                    name="email"
                                    placeholder="Votre email..."
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="password">Mot de passe</label>
                            <div className="input-wrapper">
                                <FaLock className="input-icon" />
                                <input
                                    id="password"
                                    className="input-field"
                                    type="password"
                                    name="password"
                                    placeholder="Votre mot de passe..."
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    minLength={8}
                                />
                            </div>
                        </div>

                        <div className="captcha-wrapper" style={{ 
                            marginTop: '1.5rem', 
                            marginBottom: '1.5rem',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <HCaptcha
                                ref={captchaRef}
                                sitekey="932ea1a0-e892-47b2-afb4-132ed49a8418"
                                onVerify={handleCaptchaVerify}
                                onExpire={() => setFormData({ ...formData, 'h-captcha-response': '' })}
                            />
                        </div>

                        <button
                            className="submit-button"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <svg className="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50.265" strokeDashoffset="25" />
                                    </svg>
                                    Création en cours...
                                </>
                            ) : (
                                <>
                                    Créer mon compte
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                    </svg>
                                </>
                            )}
                        </button>

                        <div className="form-footer">
                            <p>
                                Vous avez déjà un compte?{' '}
                                <a href="/" className="link-primary">Se connecter</a>
                            </p>
                        </div>
                    </form>
                    <SocialLogin />
                </div>
            </div>

            <div className="signup-right">
                <div className="hero-content">
                    <div className="hero-badge">
                        <span className="badge-dot"></span>
                        Rejoignez-nous
                    </div>
                    
                    <h1 className="hero-title">
                        Commencez votre
                        <span className="gradient-text"> voyage aujourd'hui</span>
                    </h1>
                    
                    <p className="hero-description">
                        Rejoignez des centaines d'éleveurs qui font confiance à notre plateforme pour gérer leur exploitation
                    </p>

                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon"><FaChartBar /></div>
                            <h3>Statistiques en temps réel</h3>
                            <p>Visualisez vos données instantanément</p>
                        </div>
                        
                        <div className="feature-card">
                            <div className="feature-icon"><MdMobileFriendly /></div>
                            <h3>Accès mobile</h3>
                            <p>Gérez votre ferme où que vous soyez</p>
                        </div>
                    </div>
                </div>

                <div className="bg-decoration decoration-1"></div>
                <div className="bg-decoration decoration-2"></div>
                <div className="bg-decoration decoration-3"></div>
                <div className="bg-grid"></div>
            </div>
        </div>
    );
}